import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { TenantService } from 'src/app/services/tenant/tenant.service';
import { Organisation } from 'src/types/organisation';
import { OrganisationUser } from 'src/types/organisation-user';
import { OrganisationsResponse } from 'src/types/organisations-response';
import { OrganisationResponse } from 'src/types/organisation-response';
import { AddressesResponse } from 'src/types/addresses-response';
import { OrganisationUsersResponse } from 'src/types/organisation-users-response';
import { OrganisationFilter } from 'src/types/organisation-filter';
import { OrganisationListItem } from 'src/types/organisation-list-item';
import { OrganisationPost, OrganisationPut } from 'src/types/organisation-post';
import { PostResponse } from 'src/types/post-response';
import { CisRctValues } from 'src/types/cis-rct-values';
import { OrganisationCISRCTValues } from 'src/types/organisations-cisrctvalues-response';
import { Errors } from 'src/types/errors';
import { UrlBuilder } from 'src/app/utilities/url-builder';
import { LegalEntitySummary } from 'src/types/legal-entity';
import { Bank } from 'src/types/bank-account';
import { BankAccount } from 'src/types/bank-account';

const LOCAL_STORAGE_ORGANISATION_ID_NAME = 'organisationId';

@Injectable({
  providedIn: 'root'
})
export class OrganisationService {
  private readonly organisationUsers: BehaviorSubject<OrganisationUser[]> = new BehaviorSubject<OrganisationUser[]>([]);
  
  public get organisationUsers$(): Observable<OrganisationUser[]> {
    return this.organisationUsers;
  }

  constructor(
    private readonly tenantService: TenantService,
    private readonly http: HttpClient
  ) { }

  /**
   * Gets an organisation by it's id.
   * @param id The id of the organisation.
   * @returns A response object containing the information of the organisation.
   */
  public async get(id: string | number): Promise<Organisation> {
    const apiUrl = await this.tenantService.getApiUrl();
    const url = `${apiUrl}Objects?Source=Organisation&OrganisationId=${id}`;
    const result = await firstValueFrom(this.http.get<OrganisationResponse>(url))
    return result.organisation;
  }

  /**
   * Gets all the organisations for the logged in user.
   * @returns A response object containing the organisations. 
   */
  public async getAll(filter?: OrganisationFilter): Promise<OrganisationListItem[]> {
    const apiUrl = await this.tenantService.getApiUrl();
    const url = apiUrl + 'Objects?Source=Organisations' + this.toQueryParams(filter);
    const response = await firstValueFrom(this.http.get<OrganisationsResponse>(url));
    return response.organisations;
  }

  public async getCustomers(): Promise<OrganisationListItem[]> {
    const url = new UrlBuilder(await this.tenantService.getApiUrl())
      .addResource('customers')
      .build();

    return await firstValueFrom(this.http.get<OrganisationListItem[]>(url));
  }

  /**
   * Gets the organisations linked to the users tenant
   * @returns the list of organisations that are in the scope of the current tenant organisatin
   */
  public async getTenantOrganisations(): Promise<OrganisationsResponse> {
    const apiUrl = await this.tenantService.getApiUrl();
    const url = apiUrl + 'Objects?Source=Organisations&Scope=1';
    const results = await firstValueFrom(this.http.get<OrganisationsResponse>(url));
    return results;
  }

  public async getOrganisationAddresses(): Promise<AddressesResponse> {
    const organisationId = localStorage.getItem(LOCAL_STORAGE_ORGANISATION_ID_NAME);
    if (!organisationId) { throw Errors.noSelectedOrganisation(); }
    const apiUrl = await this.tenantService.getApiUrl();
    // AddressType of 1 (Default is 'Organisation')
    const url = apiUrl + 'Objects?Source=OrganisationAddresses&OrganisationId=' + organisationId +
      "&AddressTypeId=1";
    const results = await firstValueFrom(this.http.get<AddressesResponse>(url));
    return results;
  }

  public async getOrganisationFixedAssetAddresses(): Promise<AddressesResponse> {
    const organisationId = localStorage.getItem(LOCAL_STORAGE_ORGANISATION_ID_NAME);
    if (!organisationId) { throw Errors.noSelectedOrganisation(); }

    const apiUrl = await this.tenantService.getApiUrl();
    // AddressType of 2 (is 'Fixed Asset')
    const url = apiUrl + 'Objects?Source=OrganisationAddresses&OrganisationId=' + organisationId +
      "&AddressTypeId=2";
    const results = await firstValueFrom(this.http.get<AddressesResponse>(url));
    return results;
  }

  public async getOrganisationUsers(): Promise<OrganisationUsersResponse> {
    const apiUrl = await this.tenantService.getApiUrl();
    const url = apiUrl + 'Objects?source=OrganisationUsers';
    const results = await firstValueFrom(this.http.get<OrganisationUsersResponse>(url));
    this.organisationUsers.next(results?.users ?? []);
    return results;
  }

  public async postOrganisation(organisation: OrganisationPost): Promise<number> {
    const body = { ...organisation, Type: 'Organisation' };

    const apiUrl = await this.tenantService.getApiUrl();
    const result = await firstValueFrom(this.http.post<PostResponse>(apiUrl + 'Objects', body));
    return result.objectId;
  }

  public async putOrganisation(organisation: OrganisationPut): Promise<void> {
    const body = { ...organisation, Type: 'Organisation' };
    const apiUrl = await this.tenantService.getApiUrl();
    await firstValueFrom(this.http.put(apiUrl + 'Objects', body));
  }

  public async getCISRCTValues(countryId?: number): Promise<CisRctValues[]> {
    const organisationId = localStorage.getItem(LOCAL_STORAGE_ORGANISATION_ID_NAME);
    if (!organisationId) { throw Errors.noSelectedOrganisation(); }

    const apiUrl = await this.tenantService.getApiUrl();
    let url = apiUrl + 'Objects?Source=OrganisationCISRCTValues'
    if (countryId) {
      url += '&CountryId=' + countryId;
    }

    const response = await firstValueFrom(this.http.get<OrganisationCISRCTValues>(url));
    return response.cisrctValues;
  }

  public async getLegalEntities(): Promise<LegalEntitySummary[]> {
    const url = new UrlBuilder(await this.tenantService.getApiUrl())
      .addResource('legal-entities')
      .build();
    return await firstValueFrom(this.http.get<LegalEntitySummary[]>(url));
  }

  public async getBanks(): Promise<Bank[]> {
    const url = new UrlBuilder(await this.tenantService.getApiUrl())
      .addResource('legal-entities')
      .addResource('banks')
      .build();
    return firstValueFrom(this.http.get<Bank[]>(url));
  }

  public async getSelectedLegalEntityBankAccounts(): Promise<BankAccount[]> {
    const organisationId = localStorage.getItem(LOCAL_STORAGE_ORGANISATION_ID_NAME);
    if (!organisationId) { throw Errors.noSelectedOrganisation(); }
    return (await this.get(organisationId)).bankAccounts ?? [];
  }

  private toQueryParams(filter?: OrganisationFilter): string {
    let params = '';
    if (filter?.organisationType) { params += `&OrganisationType=${filter.organisationType}`; }
    if (filter?.scope) { params += `&scope=${filter.scope}`; }
    return params;
  }
}
